import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import {
  Controller,
  type ControllerRenderProps,
  useFormContext
} from 'react-hook-form'

import {
  type AuthenticationFormValues,
  getAWSDomains,
  getExternalIntegrationForObservabilityTool,
  getUnsavedObservabilityTools,
  type ObservabilityTool,
  OBSERVABILITY_TOOLS,
  setObservabilityToolAndDomain
} from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolDomainSelect } from 'components/status-checks/form/authentication/ObservabilityToolDomainSelect'
import { ObservabilityToolMenuItemLabel } from 'components/status-checks/form/authentication/ObservabilityToolMenuItemLabel'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import {
  INITIAL_AUTHENTICATION_RESPONSE,
  useObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import {
  ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import { getIntegrationTypeFromObservabilityToolTypeAndDomain } from 'utils/team-external-integrations/team-external-integrations-util'

type FormValues = StatusCheckFormValues | AuthenticationFormValues

export const ObservabilityToolAndDomainSelect: React.FC<{
  externalIntegrations: TeamExternalIntegration[]
  isEdit: boolean
  selectedObservabilityTool: ObservabilityTool
}> = ({ externalIntegrations, isEdit, selectedObservabilityTool }) => {
  const { prometheusImprovements: hasPrometheusHealthChecks } =
    useFeatureFlags()
  const { setAuthenticationResponse, isCreatingAuthenticationOnly } =
    useObservabilityToolAuthenticationContext()

  const form = useFormContext<FormValues>()
  const { control, formState, trigger } = form

  const customObservabilityTools: ObservabilityTool[] = useMemo(
    () =>
      externalIntegrations
        .filter(
          ({ observabilityToolType }) =>
            observabilityToolType === ObservabilityToolType.CUSTOM
        )
        .map(({ domain, name }) => ({
          name,
          domains: domain
            ? [{ name: domain, integrationType: IntegrationType.Custom }]
            : [],
          type: ObservabilityToolType.CUSTOM,
          integrationType: IntegrationType.Custom
        })),
    [externalIntegrations]
  )

  const observabilityTools = useMemo(() => {
    const AWSDomains = getAWSDomains(externalIntegrations)

    return isCreatingAuthenticationOnly
      ? getUnsavedObservabilityTools(externalIntegrations, AWSDomains)
      : OBSERVABILITY_TOOLS.map(({ domains, ...sourceObservabilityTool }) => ({
          ...sourceObservabilityTool,
          domains: sourceObservabilityTool.name === 'AWS' ? AWSDomains : domains
        }))
  }, [externalIntegrations, isCreatingAuthenticationOnly])

  // Creates a Map of all observability tools keyed by the tool name
  const observabilityToolsMap = useMemo(() => {
    return [...observabilityTools, ...customObservabilityTools].reduce<
      Map<string, ObservabilityTool>
    >((map, observabilityTool) => {
      map.set(observabilityTool.name, observabilityTool)
      return map
    }, new Map())
  }, [customObservabilityTools, observabilityTools])

  const handleSelectObservabilityTool = async ({
    target
  }: SelectChangeEvent<string>) => {
    const { value } = target
    const selectedObservabilityTool = observabilityToolsMap.get(value)

    if (!selectedObservabilityTool) return

    const { domains = [], type } = selectedObservabilityTool
    const selectedDomainName = domains[0]?.name
    const integrationType = !type
      ? IntegrationType.Custom
      : getIntegrationTypeFromObservabilityToolTypeAndDomain(
          type,
          selectedDomainName
        )

    // Applicable to AWS only, pre-select the initial domain entry if it exists
    const selectedObservabilityToolWithDomain = {
      ...selectedObservabilityTool,
      ...(selectedDomainName && { selectedDomain: selectedDomainName }),
      integrationType
    }

    // This is the value we will save to the form state

    const externalIntegration = getExternalIntegrationForObservabilityTool(
      selectedObservabilityToolWithDomain,
      externalIntegrations
    )

    if (externalIntegration) {
      setObservabilityToolAndDomain(
        selectedObservabilityToolWithDomain,
        null,
        externalIntegration,
        form
      )
    }

    // Reset endpoint response
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)
  }

  const handleSelectDomain = async (
    event: SelectChangeEvent<string>,
    field: ControllerRenderProps<FormValues, 'observabilityTool.selectedDomain'>
  ) => {
    // Domain selection shouldn't be possible if no selected observability tool exists
    if (!selectedObservabilityTool) return

    const selectedDomain = selectedObservabilityTool?.domains?.find(
      ({ name }) => name === event.target.value
    )

    const updatedObservabilityToolSelection = {
      ...selectedObservabilityTool,
      ...(selectedDomain && { integrationType: selectedDomain.integrationType })
    }
    const externalIntegration = getExternalIntegrationForObservabilityTool(
      updatedObservabilityToolSelection,
      externalIntegrations
    )

    if (externalIntegration) {
      setObservabilityToolAndDomain(
        updatedObservabilityToolSelection,
        selectedDomain ?? null,
        externalIntegration,
        form
      )
    }

    // Reset endpoint response
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)

    // Validate headers whenever domain changes
    await trigger('headers')

    field.onChange(event)
  }

  return (
    <Stack spacing={2}>
      <Typography>What observability tool will your team be using?</Typography>
      <Stack direction="row" spacing={2}>
        <FormControl sx={{ maxWidth: 400, width: '100%' }}>
          <InputLabel>Observability Tool</InputLabel>
          <Controller
            control={control}
            name="observabilityTool.name"
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                data-testid="observability-tool-dropdown-select-name"
                disabled={isEdit}
                error={Boolean(error)}
                label="Observability Tool"
                labelId="select-observability-tool-label"
                onChange={handleSelectObservabilityTool}
              >
                {observabilityTools.map(({ name, integrationType }) =>
                  !hasPrometheusHealthChecks &&
                  integrationType === IntegrationType.Prometheus ? null : (
                    <MenuItem
                      key={name}
                      data-testid={`observability-tool-integration-type-${integrationType}`}
                      value={name}
                    >
                      <ObservabilityToolMenuItemLabel
                        integrationType={integrationType}
                        name={name}
                      />
                    </MenuItem>
                  )
                )}
                {customObservabilityTools.length > 0 && (
                  <ListSubheader key="custom-observability-tool-subheader">
                    Custom Observability Tools
                  </ListSubheader>
                )}
                {customObservabilityTools.map(({ name, integrationType }) => (
                  <MenuItem key={name} value={name}>
                    <ObservabilityToolMenuItemLabel
                      integrationType={integrationType}
                      name={name}
                    />
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <FormHelperText error={Boolean(formState.errors.observabilityTool)}>
            {formState.errors.observabilityTool?.message}
          </FormHelperText>
        </FormControl>
        {selectedObservabilityTool.type !== ObservabilityToolType.AWS &&
          Boolean(selectedObservabilityTool.domains?.length) && (
            <ObservabilityToolDomainSelect
              handleSelectDomain={handleSelectDomain}
              isEdit={isEdit}
              selectedObservabilityTool={selectedObservabilityTool}
            />
          )}
      </Stack>
    </Stack>
  )
}
